import { useState } from 'react';
import { toast } from 'react-toastify';
import { Button, StarRating, TmdxTextarea } from '@ds';
import { RoutePlannerModal } from '@lp/features/RoutePlanner/components/RouteModals/components/RoutePlannerModal';
import { SuggestionReviewType } from '@/api/apis/multi-suggest';
import { MAX_LENGTH_500 } from '@/constants/form.constants';
import { useCreateSuggestionReview, useUpdateSuggestionReview } from '@/queries/multi-suggest';
import { isCommentTooLong } from '@/utils/helpers';

type MultiSuggestReviewModalProps = {
  suggestionId: string;
  review?: SuggestionReviewType | null;
  isOpen: boolean;
  onCloseModal: () => void;
};

export const MultiSuggestReviewModal = ({
  suggestionId,
  review,
  isOpen,
  onCloseModal,
}: MultiSuggestReviewModalProps) => {
  const [comment, setComment] = useState<string>(review?.comment ?? '');
  const [rating, setRating] = useState<number>(review?.rating ?? 5);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [commentError, setCommentError] = useState<string>('');

  const { mutateAsync: createReview } = useCreateSuggestionReview();
  const { mutateAsync: editReview } = useUpdateSuggestionReview();

  const cleanComment = comment.trim();
  const hasCommentContent = !!cleanComment.length;

  const onCommentValueChange = (value: string) => {
    if (commentError) setCommentError('');
    if (isCommentTooLong(value, MAX_LENGTH_500)) {
      setCommentError(`Comment must be less than ${MAX_LENGTH_500} characters`);
    }
    setComment(value);
  };

  const onRatingValueChange = (value: number) => {
    setRating(value);
  };

  const handleSaveReview = async () => {
    try {
      if (!hasCommentContent) {
        setCommentError('Comment field cannot be empty or contain only whitespace');
        return;
      }

      setIsSubmitting(true);

      if (review?.id) {
        await editReview({
          id: suggestionId,
          reviewId: review.id,
          comment: cleanComment,
          rating,
        });
      } else {
        await createReview({
          id: suggestionId,
          comment: cleanComment,
          rating,
        });
      }

      onCloseModal();
    } catch (error) {
      toast.error('Error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isChangedComment = review?.comment !== cleanComment;
  const isChangedRating = review?.rating !== rating;
  const isChangedReview = isChangedComment || isChangedRating;

  const isButtonDisabled =
    !hasCommentContent || !isChangedReview || isSubmitting || isCommentTooLong(cleanComment, MAX_LENGTH_500);
  const title = review?.id ? 'Edit Review' : 'Add Review';

  return (
    <RoutePlannerModal bodyClassName="px-4 pb-4 pt-0" title={title} size="3xl" isOpen={isOpen} onClose={onCloseModal}>
      <hr className="border-t-1 border-base-50" />
      <div className="flex flex-col gap-4">
        <StarRating value={rating} onChange={onRatingValueChange} showValue />
        <TmdxTextarea
          value={comment || ''}
          onValueChange={onCommentValueChange}
          onClear={() => {
            setComment('');
            setCommentError('');
          }}
          isInvalid={!!commentError}
          errorMessage={commentError}
          classNames={{
            inputWrapper: 'bg-base-60! rounded-lg',
          }}
          aria-label="Review Comment"
          label="Comment"
          placeholder="Placeholder"
          fullWidth
          disableAnimation
          maxLength={MAX_LENGTH_500}
          minRows={5}
          description="Please provide a brief explanation (max length up to 500 characters)"
        />
      </div>
      <hr className="border-t-1 border-base-50" />
      <div className="flex self-end gap-2 items-center">
        <Button size="sm" variant="outline" onPress={onCloseModal}>
          Cancel
        </Button>
        <Button size="sm" onPress={handleSaveReview} isDisabled={isButtonDisabled}>
          Save
        </Button>
      </div>
    </RoutePlannerModal>
  );
};
